import { useCallback, useContext, useMemo } from 'react';
import TrackingContext from './tracking-context';
import { pageName, pageType } from './page-load';
import { multiply, number } from 'mathjs';
import { TrackingDataItem } from './use-tracking';
import { getPriceType } from './utils';

export function useSearchType() {
  let searchType = 'product';
  const contextValue = useContext(TrackingContext);
  searchType = useMemo(() => {
    if (contextValue.page.previousPageName.includes('interactive-periodic-table-elements')) {
      return 'element search';
    } else if (
      contextValue.page.previousPageName === pageName['search/search-result/structure'] ||
      contextValue.page.previousPageName === pageName['search/structure']
    ) {
      return 'structure search';
    } else if (contextValue.page.name === pageName['search/category']) {
      return 'catalog product';
    }
    return searchType;
  }, []);
  return searchType;
}

export function usePlacement() {
  const contextValue = useContext(TrackingContext);
  let placement = contextValue.page.type;
  const prevPageName = contextValue.page.previousPageName;
  placement = useMemo(() => {
    if (prevPageName === pageName['search/search-result/structure'] || prevPageName === pageName['search/structure']) {
      placement = 'structure search';
    } else if (prevPageName.includes('interactive-periodic-table-elements')) {
      placement = 'element search';
    }
    return placement;
  }, []);
  return placement;
}

export function usePlacementAndMethod() {
  const contextValue = useContext(TrackingContext);
  const name = contextValue.page.name;
  const prevPageName = contextValue.page.previousPageName;
  let placement = { placement: '', method: '' };
  placement = useMemo(() => {
    if (name === pageName['search/category']) {
      if (prevPageName.includes('interactive-periodic-table-elements')) {
        placement.placement = 'element search';
        placement.method = 'element search commerce-box';
      } else {
        placement.placement = 'catelog product';
        placement.method = 'catelog product commerce-box';
      }
    } else if (name === pageName['search/search-result']) {
      if (prevPageName === pageName['search/search-result/structure']) {
        placement.placement = 'structure search';
        placement.method = 'structure search commerce-box';
      } else {
        placement.placement = 'search results';
        placement.method = 'search results commerce-box';
      }
    }
    return placement;
  }, []);
  return placement;
}

export function usePdpPrevPageType() {
  const contextValue = useContext(TrackingContext);
  let prePageType = contextValue.page.previousPageType;
  if (contextValue.page.previousPageType === 'no previous value') {
    prePageType = 'direct';
  } else if (document.referrer && document.referrer.includes('interactive-periodic-table-elements')) {
    prePageType = 'element search';
  } else if (prePageType === pageType['product']) {
    prePageType = 'product detail:recommended products';
  }
  return prePageType;
}
export function usePdpPayloadTracking(
  productData,
  recommendedData: { data: any; isFetching: boolean },
  childCatalogData: { data: any; isFetching: boolean }
) {
  const placement = usePlacement();
  const prePageType = usePdpPrevPageType();
  const placementRecommend = placement === pageType['product'] ? 'product detail:recommended products' : placement;
  const prevPlacement = prePageType;

  const dataArray = useMemo(() => {
    let product;
    const items: TrackingDataItem[] = [];
    if (productData?.data?.productBaseInfo) {
      product = {
        key: 'products',
        payload: {
          name: productData?.data?.productBaseInfo.productTitle,
          familyId: productData?.data?.productBaseInfo.globalRootSku,
          catalogId: productData?.data?.productBaseInfo.catalogNumber,
          placement: prevPlacement,
        },
      };
      /* 
      keep using isFetching to check if the data is fetched. isLoading is not working as expected as "enabled" is used in useQuery
      https://github.com/TanStack/query/issues/3975
      */
      if (!recommendedData?.isFetching && !childCatalogData?.isFetching) {
        // wait for the recommended data and childCatelogData to be fetched
        items.push(product);
        const productsImp = { key: 'productImpressions', payload: [] };
        let recommendedProducts = [];
        let childProducts = [];
        if (recommendedData.data && Array.isArray(recommendedData.data) && recommendedData.data.length > 0) {
          recommendedProducts = recommendedData.data.map((val) => {
            return {
              name: val.productTitle,
              catalogId: val.catalogNumber,
              priceType: 'bestPrice',
              placement: placementRecommend,
            };
          });
        }
        if (childCatalogData.data && Array.isArray(childCatalogData.data) && childCatalogData.data.length > 0) {
          childProducts = childCatalogData.data.map((val) => {
            return {
              name: productData?.data?.productBaseInfo.productTitle,
              catalogId: val.catalogNumber,
              placement: prevPlacement,
            };
          });
        }
        productsImp.payload = [...recommendedProducts, ...childProducts];
        items.push(productsImp);
      }
    }

    return items;
  }, [productData, recommendedData, childCatalogData]);

  return dataArray;
}

export function useOrderProducts(checkoutOrderItems, productPrice, backendOrderItemPrices) {
  const placement = usePlacement();
  const priceType = getPriceType(productPrice);

  return useMemo(() => {
    if (checkoutOrderItems.length === 0) return;
    const payload = [];
    const orderProducts = {
      key: 'order',
      payload: {
        products: checkoutOrderItems.map((product) => {
          const price = productPrice[product.catalogNumber];
          const currentBackendItemPrice = backendOrderItemPrices.find(
            (item) => item.catalogNumber === product.catalogNumber
          );
          const formatPrice =
            currentBackendItemPrice?.itemSubTotal ?? multiply(number(price.bestPrice), product.quantity);
          return {
            name: product.productTitle,
            catalogId: product.catalogNumber,
            quantity: product.quantity,
            price: parseFloat(formatPrice)?.toFixed(2),
            priceType: priceType[product.catalogNumber] ?? '',
          };
        }),
      },
    };
    payload.push(orderProducts);
    const productsImp = {
      key: 'productImpressions',
      payload: checkoutOrderItems.map((product) => {
        return {
          name: product.productTitle,
          catalogId: product.catalogNumber,
          placement: placement,
        };
      }),
    };
    payload.push(productsImp);
    return payload;
  }, [checkoutOrderItems]);
}

export function useProductClickEventTracking() {
  const placement = usePlacement();
  return useCallback((product, index) => {
    const name = product.productTitle || product.itemName || product.catalogName;
    const catalogId = product.catalogNumber || product.itemCatalogNumber || product.childCatalogNumber;
    const productObj = {
      name,
      catalogId,
      placement: placement === pageType['product'] ? 'product detail:recommended products' : placement,
      position: index + 1,
    };
    const products = { products: [productObj] };
    if (sessionStorage['track']) console.log('productClickTracking', products);
    sessionStorage.setItem('c20', JSON.stringify(products));
  }, []);
}
